/**
 * Thin fetch wrapper for the TK Call Coach server API.
 *
 * Responsibilities:
 *   - Prefix every call with VITE_API_BASE_URL.
 *   - Attach the current Supabase access token as a Bearer header. The server
 *     verifies that JWT against SUPABASE_JWKS_URL and derives {userId, role,
 *     account_ref} from the profiles table — the client NEVER sends identity.
 *   - On a 401, refresh the session once and retry exactly once. If the retry
 *     also 401s, surface an ApiError so the UI can route the user to sign in.
 *   - Normalize errors into a typed ApiError ({ error, message }).
 *
 * This wrapper is intentionally minimal scaffolding. The typed endpoint helpers
 * below return shapes from types.ts; real query/mutation wiring lands with the
 * screens in Phase 5–7 (docs/MVP_BUILD_PLAN.md §7).
 */
import { supabase, getAccessToken } from "@/lib/supabase";
import type {
  AgentProfile,
  ApiError,
  AppRole,
  AiConfig,
  ApprovedUsp,
  CallDetail,
  CallLogPrivacy,
  CallType,
  CallListFilters,
  CallListPage,
  CallNote,
  CallRow,
  CallStats,
  ClassifierConfig,
  Coaching,
  FlagLifecycleRow,
  FlagLifecycleStatus,
  GraderConfig,
  LiveCoachingConfig,
  LiveReferenceCard,
  ManagerDashboard,
  ManagerFlag,
  ManagerQueueItem,
  SavedView,
  SettingsResponse,
  LiveLinesResponse,
  RubricCriterionEdit,
  RubricEditor,
  StaffMember,
  TranscriptSegment,
  ValueBuilders,
  AiReveal,
  AuditEntry,
  AuditFilters,
  Calibration,
  CalibrationFilters,
  RecordingUrl,
  SelfScore,
  SelfScoreSubmission,
  FeedbackSubmission,
  HealthStatus,
  SttEngine,
  SttCallOption,
  SttEvalBatch,
  SttReveal,
  SttLeaderRow,
  SttRatingStatus,
  ProviderSecretStatus,
} from "@/types";

// --- Admin (provisioning) shapes -------------------------------------------
export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  role: AppRole;
  active: boolean;
  can_view_live_monitor: boolean;
  extensions: string[];
}
export interface CreateUserInput {
  email: string;
  full_name: string;
  role: AppRole;
  extension_numbers: string[];
}
export interface CreateUserResult {
  user_id: string;
  email: string;
  role: AppRole;
  temp_password: string;
  warning?: string;
}

// --- Admin ops/spend config (GET/PUT /api/admin/config) ---------------------
// The ALLOWLISTED tenant_config surface (server routes/admin.ts): the two
// kill-switches, the two daily spend thresholds, and the live-capture window.

/** The voice captureGate window. The server stamps `tz` (shop timezone). */
export interface LiveCaptureHours {
  start: string; // 'HH:MM' 24h
  end: string; // 'HH:MM' 24h
  tz?: string;
}

export interface AdminOpsConfig {
  processing_enabled: boolean;
  live_capture_enabled: boolean;
  daily_cost_alert_cents: number;
  daily_cost_kill_cents: number;
  live_capture_hours: LiveCaptureHours | null;
  /** The shop's IANA timezone (display-only — the capture window runs in it). */
  timezone: string;
}

/** PUT body — every key optional; live_capture_hours sends {start,end} or null
 *  (the SERVER stamps the timezone, never the client). */
export interface AdminOpsConfigUpdate {
  processing_enabled?: boolean;
  live_capture_enabled?: boolean;
  daily_cost_alert_cents?: number;
  daily_cost_kill_cents?: number;
  live_capture_hours?: { start: string; end: string } | null;
}

// --- Admin cost dashboards (GET /api/admin/cost/*) — ADMIN-ONLY -------------
export interface CostBucket {
  cost_cents: number;
  events: number;
}

export interface CostSummary {
  range: { from: string; to: string; timezone: string };
  total_cents: number;
  event_count: number;
  total_tokens_in: number;
  total_tokens_out: number;
  total_stt_seconds: number;
  /** Ascending by local day (chart-ready). */
  by_day: Array<{ day: string } & CostBucket>;
  /** Sorted biggest spender first. Tokens are AI rows; seconds are STT rows. */
  by_category: Array<{
    category: string;
    cost_cents: number;
    events: number;
    tokens_in: number;
    tokens_out: number;
    stt_seconds: number;
  }>;
  by_provider: Array<{ provider: string } & CostBucket>;
  by_model: Array<{ provider: string; model: string } & CostBucket>;
  truncated: boolean;
}

export interface CostTopCall {
  call_id: string;
  cost_cents: number;
  events: number;
  /** Per-category cents for this call (e.g. { call_scoring: 12, stt_live: 30 }). */
  breakdown: Record<string, number>;
  started_at: string | null;
  extension_number: string | null;
  direction: string | null;
  duration_sec: number | null;
  from_number: string | null;
  to_number: string | null;
}

export interface CostTopCalls {
  range: { from: string; to: string };
  calls: CostTopCall[];
  /** Spend in the window that couldn't be tied to a calls row (reconciliation). */
  unattributed_cents: number;
  truncated: boolean;
}

/** GET /api/admin/cost/calls/:id — per-call cost drilldown (ADMIN-ONLY). */
export interface CallCostDetail {
  call_id: string;
  total_cents: number;
  /** Per-category cents (AI rows keyed by feature_key, STT as stt_batch/stt_live). */
  by_category: Record<string, number>;
  events: Array<{
    ts: string;
    category: string;
    provider: string | null;
    model: string | null;
    cost_cents: number;
    meta?: Record<string, unknown>;
  }>;
}

// --- Admin capture health (GET /api/admin/capture-health) — ADMIN-ONLY ------
export interface CaptureHealthRecentCall {
  id: string;
  started_at: string | null;
  ended_at: string | null;
  extension_number: string | null;
  direction: string | null;
  customer_phone: string | null;
  duration_ms: number | null;
  transcript_lines: number | null;
  legs_captured: Array<"rep" | "customer">;
  leg_outcomes: Partial<Record<"rep" | "customer", string>>;
  both_legs: boolean;
}

export interface CaptureHealth {
  window: { from: string; days: number; timezone: string };
  total_calls: number;
  excluded_short: number;
  qualifying_calls: number;
  both_legs_pct: number;
  both_legs_count: number;
  rep_leg_drop_pct: number;
  rep_leg_drop_count: number;
  /** Histogram of non-'ok' supervise outcomes per leg, keyed by outcome. */
  failure_reasons: Record<string, { rep: number; customer: number }>;
  by_day: Array<{ day: string; calls: number; both_legs: number; both_legs_pct: number }>;
  recent: CaptureHealthRecentCall[];
  truncated: boolean;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)
  ?.replace(/\/$/, "")
  ?? "";

// Admin impersonation: when set, every request carries `X-Act-As` and the server
// scopes the EFFECTIVE identity to this user. Only admins may use it (the server
// ignores the header for non-admins). The SimulationProvider keeps this in sync.
let actAsUserId: string | null = null;
export function setActAsUserId(id: string | null): void {
  actAsUserId = id;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiError | null;
  constructor(status: number, body: ApiError | null, message?: string) {
    super(message ?? body?.message ?? body?.error ?? `Request failed (${status})`);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Internal: prevents infinite refresh→retry recursion. */
  _isRetry?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_BASE_URL}${path}`, API_BASE_URL || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Core request. Attaches the bearer token; on 401 refreshes once and retries.
 * Returns the parsed body PLUS the response headers — GET /api/calls keeps the
 * legacy bare-array body and rides its pagination metadata on headers
 * (X-Total-Count etc., exposed via the server's CORS exposedHeaders).
 */
async function apiRequestWithHeaders<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T; headers: Headers }> {
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (actAsUserId) headers["X-Act-As"] = actAsUserId;

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !opts._isRetry) {
    // Token likely expired — refresh and retry exactly once.
    const { error } = await supabase.auth.refreshSession();
    if (!error) {
      return apiRequestWithHeaders<T>(path, { ...opts, _isRetry: true });
    }
  }

  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiRequestError(res.status, body);
  }

  // 204 / empty body tolerance.
  if (res.status === 204) return { data: undefined as T, headers: res.headers };
  const text = await res.text();
  return { data: (text ? JSON.parse(text) : undefined) as T, headers: res.headers };
}

/** Core request (body only). See apiRequestWithHeaders for the full contract. */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { data } = await apiRequestWithHeaders<T>(path, opts);
  return data;
}

/**
 * Typed endpoint helpers. These match the server route contract 1:1 so screens
 * and TanStack Query hooks call them by name. Bodies/shapes come from types.ts.
 */
export const api = {
  health: () => apiRequest<HealthStatus>("/healthz"),

  listCalls: (filters: CallListFilters = {}, signal?: AbortSignal) =>
    apiRequest<CallRow[]>("/api/calls", { query: { ...filters }, signal }),

  // Paged list: the same bare-array body plus the header-borne pagination
  // metadata (X-Total-Count / X-Offset / X-Has-More / X-Scan-Truncated). The
  // dashboard uses THIS; the bare listCalls stays for callers that only want
  // the first page (legacy behavior).
  listCallsPaged: async (
    filters: CallListFilters = {},
    signal?: AbortSignal,
  ): Promise<CallListPage> => {
    const { data, headers } = await apiRequestWithHeaders<CallRow[]>("/api/calls", {
      query: { ...filters },
      signal,
    });
    const rows = data ?? [];
    const total = Number(headers.get("X-Total-Count"));
    const offset = Number(headers.get("X-Offset"));
    return {
      rows,
      total: Number.isFinite(total) ? total : rows.length,
      offset: Number.isFinite(offset) ? offset : filters.offset ?? 0,
      hasMore: headers.get("X-Has-More") === "true",
      scanTruncated: headers.get("X-Scan-Truncated") === "true",
    };
  },

  // Role-aware dashboard KPI aggregates over the caller's visible set (same
  // filters as listCalls). AI-derived numbers honor the self-score reveal gate.
  getCallStats: (filters: CallListFilters = {}, signal?: AbortSignal) =>
    apiRequest<CallStats>("/api/calls/stats", { query: { ...filters }, signal }),

  getCall: (id: string, signal?: AbortSignal) =>
    apiRequest<CallDetail>(`/api/calls/${id}`, { signal }),

  getTranscript: (id: string, signal?: AbortSignal) =>
    apiRequest<TranscriptSegment[]>(`/api/calls/${id}/transcript`, { signal }),

  // Submitting the self-score LOCKS it and unlocks the AI report + manager
  // coaching for the owning rep (managers/admins are never gated).
  submitSelfScore: (id: string, body: SelfScoreSubmission) =>
    apiRequest<{ total: number }>(`/api/calls/${id}/self-score`, {
      method: "POST",
      body,
    }),

  // Current self-score (draft or submitted) plus the rubric to render against.
  getSelfScore: (id: string, signal?: AbortSignal) =>
    apiRequest<SelfScore>(`/api/calls/${id}/self-score`, { signal }),

  // 403 if the viewer cannot reveal AI yet (gate not satisfied).
  getAiReveal: (id: string, signal?: AbortSignal) =>
    apiRequest<AiReveal>(`/api/calls/${id}/ai-reveal`, { signal }),

  getRecordingUrl: (id: string) =>
    apiRequest<RecordingUrl>(`/api/calls/${id}/recording-url`),

  // Flag/unflag a call for later AI transcription + analysis (marker only —
  // nothing is processed at request time). Server scopes by role/ownership.
  setReviewFlag: (id: string, requested: boolean) =>
    apiRequest<{ review_requested: boolean }>(`/api/calls/${id}/review`, {
      method: "POST",
      body: { requested },
    }),

  // Timestamped feedback note keyed to the audio. start_ms null = whole-call.
  // Returns the created note so the UI can append it without a refetch.
  addCallNote: (
    id: string,
    body: { start_ms: number | null; text: string; segment_id?: number },
  ) =>
    apiRequest<CallNote>(`/api/calls/${id}/annotations`, {
      method: "POST",
      body,
    }),

  listCallNotes: (id: string, signal?: AbortSignal) =>
    apiRequest<CallNote[]>(`/api/calls/${id}/annotations`, { signal }),

  submitFeedback: (body: FeedbackSubmission) =>
    apiRequest<void>("/api/feedback", { method: "POST", body }),

  // ── Manager coaching tools (server enforces role: manager/admin only) ──────
  getManagerDashboard: (signal?: AbortSignal) =>
    apiRequest<ManagerDashboard>("/api/manager/dashboard", { signal }),

  // Per-rep self-vs-AI calibration (CP1). Same window semantics as the dashboard
  // stats (from/to). Respects the call-log blocklist for non-admins server-side.
  getCalibration: (filters: CalibrationFilters = {}, signal?: AbortSignal) =>
    apiRequest<Calibration>("/api/manager/calibration", {
      query: { ...filters },
      signal,
    }),

  // Upsert the single manager flag for a call (exemplary / needs_work). `status`
  // lets a manager close (done) or reopen (open) the coaching item; `shared`
  // exposes exemplary calls to staff. Returns the upserted flag row.
  setFlag: (
    id: string,
    body: {
      type: "exemplary" | "needs_work";
      note?: string;
      shared?: boolean;
      status?: "open" | "done";
    },
  ) => apiRequest<ManagerFlag>(`/api/calls/${id}/flag`, { method: "POST", body }),

  // Assign the call's coaching to a rep (upserts the same flag row, setting
  // assigned_to / due_date / note). Returns the upserted flag row.
  assignCall: (
    id: string,
    body: { assigned_to: string; due_date?: string; note?: string },
  ) => apiRequest<ManagerFlag>(`/api/calls/${id}/assign`, { method: "POST", body }),

  // Replace the manager's final score of record (one override per call). `note`
  // is required and shown to the rep. Returns { id, total }.
  overrideScore: (
    id: string,
    body: {
      total: number;
      note: string;
      items: Array<{ criterion_key: string; points: number; note?: string }>;
    },
  ) =>
    apiRequest<{ id: string; total: number }>(`/api/calls/${id}/override`, {
      method: "POST",
      body,
    }),

  // Manager re-classifies a call (call-type override) -> re-points the rubric and
  // re-runs the score stage. Manager/admin only.
  overrideCallType: (id: string, callType: CallType) =>
    apiRequest<{ ok: true; call_type: CallType; rescored: boolean }>(
      `/api/calls/${id}/call-type`,
      { method: "POST", body: { call_type: callType } },
    ),

  // Manager re-attributes a call to the rep who handled it (rep override). Pass
  // null to clear. Re-attributes only — does NOT re-run the AI score.
  overrideRep: (id: string, repOverrideId: string | null) =>
    apiRequest<{ ok: true }>(`/api/calls/${id}/rep`, {
      method: "POST",
      body: { rep_override_id: repOverrideId },
    }),

  // Manager excludes/un-excludes a call from stats. Un-excluding re-runs scoring.
  toggleExclude: (id: string, excluded: boolean, reason?: string) =>
    apiRequest<{ ok: true; excluded: boolean; rescored: boolean }>(
      `/api/calls/${id}/exclude`,
      { method: "POST", body: { excluded, reason } },
    ),

  // Flag + override for a call, for the coaching display panel.
  getCoaching: (id: string, signal?: AbortSignal) =>
    apiRequest<Coaching>(`/api/calls/${id}/coaching`, { signal }),

  // Open coaching items (calls with an open flag), newest first.
  getManagerQueue: (signal?: AbortSignal) =>
    apiRequest<ManagerQueueItem[]>("/api/manager/queue", { signal }),

  // Active staff/manager profiles for the assign dropdown.
  getManagerStaff: (signal?: AbortSignal) =>
    apiRequest<StaffMember[]>("/api/manager/staff", { signal }),

  // Coaching-lifecycle transition (CP3). Managers move a flag forward along the
  // chain (skips allowed) or reopen a closed one; the assigned REP's single
  // allowed call is status='acknowledged' on their own flag (reveal-gated
  // server-side). An illegal move returns 409 { error:'illegal_transition' }.
  // Pass flagId when the caller knows the exact row (inbox/agent-profile tables)
  // — a call can carry BOTH a manual and an auto_review flag, and the explicit
  // id guarantees the right one moves. Without it the server prefers the open
  // manual flag, then the open auto_review row.
  transitionFlag: (id: string, status: FlagLifecycleStatus, flagId?: string) =>
    apiRequest<FlagLifecycleRow>(`/api/calls/${id}/flag`, {
      method: "PATCH",
      body: { status, ...(flagId ? { flag_id: flagId } : {}) },
    }),

  // Per-rep development rollup (CP3 agent profile). Manager/admin only; same
  // window semantics as calibration (from/to on the call's started_at).
  getAgentProfile: (
    id: string,
    filters: CalibrationFilters = {},
    signal?: AbortSignal,
  ) =>
    apiRequest<AgentProfile>(`/api/manager/agents/${id}`, {
      query: { ...filters },
      signal,
    }),

  // ── Saved views (per-user; any role) ────────────────────────────────────────
  // Personal named filter bundles for the Dashboard. Owner-only server-side;
  // applying one just replays normal GET /api/calls filters (every gate intact).
  listSavedViews: (signal?: AbortSignal) =>
    apiRequest<SavedView[]>("/api/views", { signal }),

  // 409 name_taken on a duplicate name; 400 too_many_views at the per-user cap.
  createSavedView: (body: {
    name: string;
    filters: Record<string, string | number | boolean>;
  }) => apiRequest<SavedView>("/api/views", { method: "POST", body }),

  deleteSavedView: (id: string) =>
    apiRequest<void>(`/api/views/${id}`, { method: "DELETE" }),

  // Admin provisioning (server enforces admin role).
  adminListUsers: (signal?: AbortSignal) =>
    apiRequest<{ users: AdminUser[] }>("/api/admin/users", { signal }),

  adminCreateUser: (body: CreateUserInput) =>
    apiRequest<CreateUserResult>("/api/admin/users", { method: "POST", body }),

  adminCreateExtension: (body: { user_id: string; extension_number: string }) =>
    apiRequest<{ ok: boolean }>("/api/admin/extensions", { method: "POST", body }),

  adminResetPassword: (userId: string) =>
    apiRequest<{ user_id: string; temp_password: string }>(
      `/api/admin/users/${userId}/reset-password`,
      { method: "POST", body: {} },
    ),

  adminSetLiveMonitor: (userId: string, enabled: boolean) =>
    apiRequest<{ user_id: string; can_view_live_monitor: boolean }>(
      `/api/admin/users/${userId}/live-monitor`,
      { method: "POST", body: { enabled } },
    ),

  // Soft deactivate/reactivate (one toggle endpoint). Deactivation locks the
  // user out on every request (403 account_deactivated) but keeps their calls,
  // scores, and coaching history intact. The server rejects self-deactivation.
  adminSetUserActive: (userId: string, active: boolean) =>
    apiRequest<{ user_id: string; active: boolean }>(
      `/api/admin/users/${userId}/active`,
      { method: "POST", body: { active } },
    ),

  // ── Ops/spend config + cost & capture dashboards (admin only) ──────────────
  // GET/PUT the ALLOWLISTED ops keys (kill-switches, spend thresholds, capture
  // window). The PUT rejects alert > kill with error 'alert_threshold_above_kill'.
  adminGetOpsConfig: (signal?: AbortSignal) =>
    apiRequest<AdminOpsConfig>("/api/admin/config", { signal }),

  adminSaveOpsConfig: (body: AdminOpsConfigUpdate) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/config", {
      method: "PUT",
      body,
    }),

  // Spend rollups over v_cost_events (every dollar: Claude + STT). Range
  // defaults to the last 30 days server-side when from/to are omitted.
  adminCostSummary: (range: { from?: string; to?: string } = {}, signal?: AbortSignal) =>
    apiRequest<CostSummary>("/api/admin/cost/summary", { query: { ...range }, signal }),

  // Top-N most-expensive calls in the window (links to CallDetail).
  adminCostTopCalls: (
    q: { from?: string; to?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => apiRequest<CostTopCalls>("/api/admin/cost/calls", { query: { ...q }, signal }),

  // Per-call cost drilldown (STT + AI events for ONE call). ADMIN-ONLY on the
  // server (requireAdmin) — the CallDetail cost line renders nothing for
  // non-admins and never calls this.
  adminCostForCall: (id: string, signal?: AbortSignal) =>
    apiRequest<CallCostDetail>(`/api/admin/cost/calls/${id}`, { signal }),

  // Live-capture quality aggregates (both-legs %, rep drops, failure reasons).
  adminCaptureHealth: (
    q: { days?: number; limit?: number } = {},
    signal?: AbortSignal,
  ) => apiRequest<CaptureHealth>("/api/admin/capture-health", { query: { ...q }, signal }),

  // Bulk re-score (estimate first, then run). Reuses stored transcripts —
  // resets call_type and re-enqueues classify+score; NEVER re-runs STT.
  adminRescoreEstimate: (filter?: { from?: string; to?: string }) =>
    apiRequest<{ count: number; est_cost_cents: number }>("/api/admin/rescore", {
      method: "POST",
      body: { mode: "estimate", ...(filter ? { filter } : {}) },
    }),

  adminRescoreRun: (filter?: { from?: string; to?: string }) =>
    apiRequest<{ enqueued: number }>("/api/admin/rescore", {
      method: "POST",
      body: { mode: "run", ...(filter ? { filter } : {}) },
    }),

  // AI scoring config (model + system prompt). Server enforces admin role.
  adminGetAiConfig: (signal?: AbortSignal) =>
    apiRequest<AiConfig>("/api/admin/ai-config", { signal }),

  // Pass system_prompt: "" to fall back to the built-in DEFAULT_SYSTEM_PROMPT.
  adminSaveAiConfig: (body: { model?: string; system_prompt?: string }) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/ai-config", { method: "PUT", body }),

  // AI audit log viewer (admin only — server enforces the role). Tenant-scoped,
  // filtered, newest first, with an up/down feedback summary per row.
  adminGetAudit: (filters: AuditFilters = {}, signal?: AbortSignal) =>
    apiRequest<{ rows: AuditEntry[] }>("/api/admin/audit", {
      query: { ...filters },
      signal,
    }),

  // ── STT engine A/B compare + provider keys (admin/manager) ─────────────────
  sttEngines: (signal?: AbortSignal) =>
    apiRequest<{ engines: SttEngine[] }>("/api/admin/stt/engines", { signal }),
  sttCalls: (signal?: AbortSignal) =>
    apiRequest<{ calls: SttCallOption[] }>("/api/admin/stt/calls", { signal }),
  sttEvalBatch: (callId: string, signal?: AbortSignal) =>
    apiRequest<SttEvalBatch>("/api/admin/stt/eval", { query: { call_id: callId }, signal }),
  sttRun: (body: { call_id: string; variants: { provider: string; model: string }[] }) =>
    apiRequest<SttEvalBatch>("/api/admin/stt/eval/run", { method: "POST", body }),
  sttRate: (runId: string, body: { score: number; status: SttRatingStatus }) =>
    apiRequest<{ ok: boolean }>(`/api/admin/stt/eval/${runId}/rating`, { method: "POST", body }),
  // Bracket: record a pairwise pick (the winner of one "test").
  sttMatchup: (body: {
    call_id: string;
    run_a_id: string;
    run_b_id: string;
    winner_run_id: string;
    round: number;
  }) => apiRequest<{ ok: boolean }>("/api/admin/stt/eval/matchup", { method: "POST", body }),
  sttReveal: (callId: string, signal?: AbortSignal) =>
    apiRequest<{ reveal: SttReveal[] }>(`/api/admin/stt/eval/${callId}/reveal`, { signal }),
  sttAggregate: (signal?: AbortSignal) =>
    apiRequest<{ leaderboard: SttLeaderRow[] }>("/api/admin/stt/aggregate", { signal }),
  // Provider API keys (admin only on the server). GET returns is_set + last4 only.
  sttSecrets: (signal?: AbortSignal) =>
    apiRequest<{ secrets: ProviderSecretStatus[] }>("/api/admin/stt/secrets", { signal }),
  sttSetSecret: (body: { provider: string; api_key: string }) =>
    apiRequest<{ ok: boolean; secrets: ProviderSecretStatus[] }>("/api/admin/stt/secrets", {
      method: "PUT",
      body,
    }),

  // ── AI Settings hub (admin only — server enforces the role) ────────────────
  // All of these mirror adminGetAiConfig/adminSaveAiConfig: GET returns the
  // current override-or-default values; PUT merges into tenant_config (passing
  // a *_system_prompt of "" CLEARS the override → built-in default).

  // After-call grader: extends ai-config with per-call-type guidance. Reuses
  // the SAME /api/admin/ai-config route; PUT also accepts a per-type
  // `type_guidance` map ('' clears that type back to the code default).
  adminGetGraderConfig: (signal?: AbortSignal) =>
    apiRequest<GraderConfig>("/api/admin/ai-config", { signal }),

  adminSaveGraderConfig: (body: {
    model?: string;
    system_prompt?: string;
    type_guidance?: { [callType: string]: string };
  }) => apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/ai-config", { method: "PUT", body }),

  // Per-call-type rubric editor. GET reads the CURRENT version for the type;
  // PUT writes a NEW version (weights must sum to exactly 100) and returns it.
  adminGetRubric: (callType: CallType, signal?: AbortSignal) =>
    apiRequest<RubricEditor>("/api/admin/rubric", {
      query: { call_type: callType },
      signal,
    }),

  adminSaveRubric: (body: {
    call_type: CallType;
    note?: string;
    criteria: RubricCriterionEdit[];
  }) => apiRequest<{ version: number }>("/api/admin/rubric", { method: "PUT", body }),

  // Call-type classifier (model + prompt + editable staff roster).
  adminGetClassifierConfig: (signal?: AbortSignal) =>
    apiRequest<ClassifierConfig>("/api/admin/classifier-config", { signal }),

  adminSaveClassifierConfig: (body: {
    model?: string;
    system_prompt?: string;
    staff_first_names?: string[];
  }) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/classifier-config", {
      method: "PUT",
      body,
    }),

  // Value-builders: approved USPs + review thresholds + excluded call types.
  adminGetValueBuilders: (signal?: AbortSignal) =>
    apiRequest<ValueBuilders>("/api/admin/value-builders", { signal }),

  adminSaveValueBuilders: (body: {
    approved_usps?: ApprovedUsp[];
    manager_review_below?: number;
    manager_review_critical_misses?: string[];
    scoring_excludes_call_types?: string[];
  }) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/value-builders", {
      method: "PUT",
      body,
    }),

  // Live coaching: master switch + model + prompt + cadence + reference cards.
  adminGetLiveConfig: (signal?: AbortSignal) =>
    apiRequest<LiveCoachingConfig>("/api/admin/live-coaching-config", { signal }),

  adminSaveLiveConfig: (body: {
    enabled?: boolean;
    model?: string;
    system_prompt?: string;
    max_per_call?: number;
    min_gap_ms?: number;
    ttl_ms?: number;
    eval_floor_ms?: number;
    reference_cards?: LiveReferenceCard[];
    gui_v2_enabled?: boolean;
    system_prompt_v2?: string;
  }) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/live-coaching-config", {
      method: "PUT",
      body,
    }),

  // ── General settings (Operations + Advanced tabs) ──────────────────────────
  // The schema-driven tenant_config surface. GET ships the schema + current
  // values; PUT sends only the changed keys ({updates}) and returns {ok,changed}
  // — callers rely on invalidateQueries for the authoritative re-read.
  adminGetSettings: (signal?: AbortSignal) =>
    apiRequest<SettingsResponse>("/api/admin/settings", { signal }),

  adminSaveSettings: (updates: Record<string, unknown>) =>
    apiRequest<{ ok: boolean; changed: string[] }>("/api/admin/settings", {
      method: "PUT",
      body: { updates },
    }),

  // ── Live coaching panel (any authenticated role) ───────────────────────────
  // The tenant's REAL reference cards (tenant_config live_reference_cards, the
  // set edited in Admin → Live coaching). `cards: []` = no override stored —
  // the panel then falls back to its bundled defaults (mirrors the voice
  // engine's "[] => DEFAULT_REFERENCE_CARDS" resolution).
  liveReferenceCards: (signal?: AbortSignal) =>
    apiRequest<{ cards: LiveReferenceCard[] }>("/api/live/reference-cards", { signal }),

  // The watchable live lines (coached extensions + rep names) for the line
  // selector. Any authenticated user during the test phase.
  liveLines: (signal?: AbortSignal) =>
    apiRequest<LiveLinesResponse>("/api/live/lines", { signal }),

  // Call-log privacy: the admin-managed blocklist of phone numbers whose calls
  // are hidden from the reviewer for staff + managers (admins always see them;
  // the live coach is unaffected). GET returns the raw saved list; PUT returns
  // {ok, blocked_count} (NOT the full config) — callers rely on invalidateQueries
  // (mirrors the other admin tabs' fixed pattern), never setQueryData the PUT.
  adminGetCallLogPrivacy: (signal?: AbortSignal) =>
    apiRequest<CallLogPrivacy>("/api/admin/call-log-privacy", { signal }),

  adminSaveCallLogPrivacy: (blocked_numbers: string[]) =>
    apiRequest<{ ok: boolean; blocked_count: number }>("/api/admin/call-log-privacy", {
      method: "PUT",
      body: { blocked_numbers },
    }),

  // Staff call visibility (Security center → Privacy & access). When true, staff
  // review ALL team calls + content, not just their own line. GET returns the
  // current flag; PUT persists it (callers rely on invalidateQueries).
  adminGetCallVisibility: (signal?: AbortSignal) =>
    apiRequest<{ staff_full_call_access: boolean }>("/api/admin/call-visibility", {
      signal,
    }),

  adminSaveCallVisibility: (staff_full_call_access: boolean) =>
    apiRequest<{ ok: boolean; staff_full_call_access: boolean }>(
      "/api/admin/call-visibility",
      { method: "PUT", body: { staff_full_call_access } },
    ),
};
